import { useMemo } from "react";
import { Field, Label, Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import RequiredField from "../RequiredField";
import type { SimpleOption } from "./SimpleListbox";

interface MultiSelectDropDownProps {
	id: string;
	label?: string;
	requiredField?: boolean;

	values: Array<string | number>;
	setValues: (values: Array<string | number>) => void;

	options: SimpleOption[];

	fieldClasses?: string;
	labelClasses?: string;
	buttonClasses?: string;
	panelClasses?: string;

	placeholder?: string;
	maxLabelItems?: number;
	renderOption?: (option: SimpleOption) => React.ReactNode;
}

const keyOf = (v: string | number) => String(v);

export default function MultiSelectDropDown({
	id,
	label,
	requiredField,
	values,
	setValues,
	options,
	fieldClasses,
	labelClasses,
	buttonClasses,
	panelClasses,
	placeholder = "All",
	maxLabelItems = 2,
	renderOption,
}: MultiSelectDropDownProps) {
	const selectedOptions = useMemo(() => {
		const valueKeys = new Set(values.map(keyOf));
		return options.filter((o) => valueKeys.has(keyOf(o.value)));
	}, [options, values]);

	const buttonContent = useMemo(() => {
		if (selectedOptions.length === 0) return placeholder;
		if (selectedOptions.length > maxLabelItems) {
			return `${selectedOptions.length} selected`;
		}

		if (renderOption) {
			return selectedOptions.map((opt, index) => (
				<span key={String(opt.value)} className="inline-flex items-center">
					{index > 0 && <span className="mr-1">,</span>}
					{renderOption(opt)}
				</span>
			));
		}

		return selectedOptions.map((o) => o.label).join(", ");
	}, [selectedOptions, placeholder, maxLabelItems, renderOption]);

	const isSelected = (value: string | number) => values.some((v) => keyOf(v) === keyOf(value));

	const toggle = (value: string | number) => {
		if (isSelected(value)) {
			setValues(values.filter((v) => keyOf(v) !== keyOf(value)));
			return;
		}

		const opt = options.find((o) => keyOf(o.value) === keyOf(value));
		setValues([...values, opt?.value ?? value]);
	};

	const clearAll = () => setValues([]);

	return (
		<Field className={fieldClasses}>
			{label && (
				<Label
					htmlFor={id}
					className={`${labelClasses ? `${labelClasses} ` : ""}block mb-2 text-sm font-medium text-gray-90`}>
					{label}
					{requiredField && <RequiredField />}
				</Label>
			)}

			<Popover className="relative">
				<PopoverButton
					id={id}
					className={
						buttonClasses ??
						`relative w-full h-9 rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-10 text-left text-sm
            text-gray-900 focus:border-primary-600 focus:outline-none focus:ring-1 focus:ring-primary-600 truncate`
					}>
					<span
						className={`block ${
							renderOption &&
							selectedOptions.length > 0 &&
							selectedOptions.length <= maxLabelItems
								? "flex items-center whitespace-nowrap truncate"
								: "truncate"
						}`}>
						{buttonContent}
					</span>
					<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
						<ChevronDownIcon className="h-5 text-gray-400 pr-3" />
					</span>
				</PopoverButton>

				<PopoverPanel
					className={
						panelClasses ??
						`absolute z-50 mt-1 w-full overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none`
					}>
					<div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
						<div className="text-xs text-gray-500">
							{selectedOptions.length
								? `${selectedOptions.length} selected`
								: "0 selected"}
						</div>

						<button
							type="button"
							onClick={clearAll}
							disabled={selectedOptions.length === 0}
							className="text-xs text-primary-800 hover:text-primary-900 disabled:text-gray-300 disabled:cursor-auto!">
							Clear
						</button>
					</div>

					<div className="max-h-60 overflow-auto py-1">
						{options.length === 0 && (
							<div className="px-3 py-2 text-sm text-gray-400">No options</div>
						)}

						{options.map((opt) => {
							const selected = isSelected(opt.value);
							const disabled = opt.disabled ?? false;

							return (
								<button
									key={String(opt.value)}
									type="button"
									disabled={disabled}
									onClick={() => toggle(opt.value)}
									className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left
                    ${
						disabled
							? "text-gray-400 cursor-default"
							: "text-gray-900 hover:bg-primary-100"
					}
                    ${selected ? "bg-primary-100" : ""}`}>
									<span
										className={`block truncate ${
											selected ? "font-medium" : "font-normal"
										}`}>
										{renderOption ? renderOption(opt) : opt.label}
									</span>
									{selected && <CheckIcon className="h-4 w-4 text-primary-800" />}
								</button>
							);
						})}
					</div>
				</PopoverPanel>
			</Popover>
		</Field>
	);
}
